import { useState, useRef } from "react";
import { Camera, Upload, X, Loader2, Image } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface GiftPhoto {
  id: string;
  photo_url: string;
  caption: string | null;
  uploaded_by_name: string;
  uploaded_at: string;
}

interface GiftPhotoGalleryProps {
  requestId: string;
  photos: GiftPhoto[];
  onPhotoUploaded: () => void;
  participantName?: string;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function GiftPhotoGallery({ 
  requestId, 
  photos, 
  onPhotoUploaded,
  participantName 
}: GiftPhotoGalleryProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [caption, setCaption] = useState("");
  const [uploaderName, setUploaderName] = useState(participantName || "");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setShowUploadForm(true);
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploaderName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    setIsUploading(true);

    try {
      // Upload to storage
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${requestId}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('gift-photos')
        .upload(fileName, selectedFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('gift-photos')
        .getPublicUrl(fileName);

      // Save to database
      const { error: dbError } = await supabase
        .from('gift_photos')
        .insert({
          request_id: requestId,
          photo_url: publicUrl,
          caption: caption.trim() || null,
          uploaded_by_name: uploaderName.trim(),
        });

      if (dbError) throw dbError;

      toast.success("Photo uploaded! 📸");
      setShowUploadForm(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      setCaption("");
      onPhotoUploaded();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload photo. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const cancelUpload = () => {
    setShowUploadForm(false);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setCaption("");
  };

  return (
    <Card className="border-customer/20 bg-gradient-to-br from-customer/5 to-purple-50/50 dark:from-customer/10 dark:to-purple-950/20">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-customer" />
            <h3 className="font-semibold text-foreground">Gift Moments 📸</h3>
          </div>
          
          {!showUploadForm && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              className="border-customer/30 text-customer hover:bg-customer/10"
            >
              <Upload className="w-4 h-4 mr-2" />
              Add Photo
            </Button>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>

        {/* Upload Form */}
        {showUploadForm && previewUrl && (
          <div className="space-y-4 mb-4 p-4 bg-white/50 dark:bg-gray-900/50 rounded-lg border border-customer/20">
            <div className="relative">
              <img 
                src={previewUrl} 
                alt="Preview" 
                className="w-full max-h-64 object-contain rounded-lg"
              />
              <button
                onClick={cancelUpload}
                className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <Input
              placeholder="Your name"
              value={uploaderName}
              onChange={(e) => setUploaderName(e.target.value)}
              className="border-customer/30"
            />
            
            <Input
              placeholder="Add a caption (optional)"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="border-customer/30"
            />
            
            <div className="flex gap-2">
              <Button
                onClick={handleUpload}
                disabled={isUploading || !uploaderName.trim()}
                className="flex-1 bg-customer hover:bg-customer/90 text-customer-foreground"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Share Photo
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={cancelUpload}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Photo Gallery */}
        {photos.length > 0 ? (
          <div className="grid grid-cols-2 gap-3">
            {photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img
                  src={photo.photo_url}
                  alt={photo.caption || "Gift moment"}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute bottom-2 left-2 right-2 text-white">
                    <p className="text-xs font-medium truncate">{photo.uploaded_by_name}</p>
                    {photo.caption && (
                      <p className="text-xs text-white/80 truncate">{photo.caption}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : !showUploadForm && (
          <div className="text-center py-6 text-muted-foreground">
            <Image className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No photos yet</p>
            <p className="text-xs">Be the first to share a gift moment!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
